// Public live schedule: ONE calendar for every page that shows the weekly live classes to
// people who have not paid for them — homepage, the pricing pages, the Live upgrade page,
// /live-pricing-options and /live-classes-schedule. Read-only: no bookings, no Zoom links
// (the Library and the Vidapp page keep their own, member-only calendars).
//
// On a Kajabi page:
//
//   <div data-dmc-live-schedule></div>                      draws the calendar here
//   <button data-dmc-live-schedule-open>Schedule</button>   opens it in the popup
//   <script src="https://cdn2.dance-masterclass.com/liveSchedulePublic.js"></script>
//
// Options, as data- attributes on either element (or as an object to mount/open):
//   data-cta="auto|show|hide"   Get Started in the class dialog. auto (default) = hidden on
//                               any path containing "pricing", shown everywhere else.
//   data-cta-url="/pricing-eur" where Get Started goes (default: pricing of the visitor's
//                               currency).
//   data-scroll="page|self"     page (default): grows with its content. self: fills the
//                               container and scrolls inside it (a modal body).
//   data-sticky-top="80"        px under a fixed page header for the pinned day row.
//   data-interactive="false"    cards do not open the dialog (a clipped sneak peek).
//   data-how-it-works="false"   hides the "How it works" trailer button in the top bar
//                               (the homepage's OLC header has its own).
//   data-see-pricing="true"     "See Pricing" link after "How it works" (same destination as
//                               Get Started). Off by default: pricing pages do not need it.
//   data-surface="homepage"     sent with the Get Started dataLayer event (default: path).
//
// From JS: DMCLiveSchedule.mount(elOrSelector, opts) → { refresh, destroy },
// DMCLiveSchedule.open(opts), DMCLiveSchedule.close(), DMCLiveSchedule.scan(rootEl).
// Pages whose own script runs before this file can wait for 'dmc-live-schedule:ready'.

import { mountCalendar } from './live-schedule-public/calendar';
import { closePopup, openPopup } from './live-schedule-public/popup';
import { closeDetail } from './live-schedule-public/detail';

const VERSION = '1.4.1';

const DEFAULTS = {
  cta: 'auto',
  ctaUrl: '',
  scroll: 'page',
  stickyTop: 0,
  interactive: true,
  howItWorks: true,
  seePricing: false,
  surface: '',
};

function withDefaults(opts) {
  const o = { ...DEFAULTS, ...(opts || {}) };
  if (!o.surface) o.surface = window.location.pathname;
  return o;
}

function optionsFrom(el) {
  const d = el.dataset;
  const o = {};
  if (d.cta) o.cta = d.cta;
  if (d.ctaUrl) o.ctaUrl = d.ctaUrl;
  if (d.scroll) o.scroll = d.scroll;
  if (d.stickyTop) o.stickyTop = Number(d.stickyTop) || 0;
  if (d.interactive) o.interactive = d.interactive !== 'false';
  if (d.howItWorks) o.howItWorks = d.howItWorks !== 'false';
  if (d.seePricing) o.seePricing = d.seePricing === 'true';
  if (d.surface) o.surface = d.surface;
  return o;
}

function mount(target, opts) {
  const el =
    typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) {
    console.warn('[dmc-live-schedule] mount target not found:', target);
    return null;
  }
  return mountCalendar(
    el,
    withDefaults({ ...optionsFrom(el), ...(opts || {}) }),
  );
}

function scan(rootEl) {
  (rootEl || document)
    .querySelectorAll('[data-dmc-live-schedule]')
    .forEach((el) => mount(el));
}

function init() {
  // Loaded twice (a header field and a page both include it): the first copy wins.
  if (window.DMCLiveSchedule) return;

  window.DMCLiveSchedule = {
    version: VERSION,
    mount,
    open: (opts) => openPopup(withDefaults(opts)),
    close: closePopup,
    scan,
  };

  document.addEventListener('click', (e) => {
    const trigger =
      e.target.closest && e.target.closest('[data-dmc-live-schedule-open]');
    if (!trigger) return;
    e.preventDefault();
    openPopup(withDefaults(optionsFrom(trigger)));
  });

  // Escape closes the top layer only: the class dialog first, then the popup.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !closeDetail()) closePopup();
  });

  const start = () => {
    scan();
    window.dispatchEvent(new CustomEvent('dmc-live-schedule:ready'));
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}

init();
